<template>
	<div class="basket-form-cost text-center h5">
		<div>
			Ваш заказ:
			<span class="ml-1">{{ itemsCost | nf }} ₽</span>
		</div>
		<div v-if="form.POSTCARD_ID">
			Открытка:
			<span class="ml-1">{{ postcardPrice | nf }} ₽</span>
		</div>
		<template v-if="deliveryCost">
			<div>
				Доставка:
				<span class="ml-1">{{ deliveryCost | nf }} ₽</span>
			</div>
		</template>
		<free-delivery-message :form="form" break-after-price/>
    <template v-if="discount">
      <div>
        Ваша выгода:
        <span class="ml-1">{{ discount | nf }} ₽</span>
      </div>
    </template>
    <template v-if="prepaymentNotificationText">
      <div class="basket-form-cost__prepayment-notification size1 mt-4">
<!--        <v-icon small class="mr-1 mt-1">mdi-information-outline</v-icon>-->
        <span v-html="prepaymentNotificationText"></span>
      </div>
    </template>
		<template v-if="deliveryCost===null">
			<p class="basket-form-cost__note limited mt-8 mx-auto">Стоимость доставки и итоговая стоимость заказа<br/>будет
				рассчитана для Вас менеджером<br/>после оформления заказа</p>
		</template>
		<h3 v-else class="mt-4">ИТОГО: <span class="ml-1">{{ total | nf }} ₽</span></h3>
	</div>
</template>

<script>
import {destinations, deliveryTypes} from '@/store/delivery';
import city from "@/mixins/city";
import freeDelivery, {itemsFullPrice} from "@/mixins/free-delivery";
import {isFreeItem} from "@/mixins/free-item";
import FreeDeliveryMessage from "@/views/Basket/free-delivery-message.vue";

export default {
	name: "basket-form-cost",
	components: {FreeDeliveryMessage},
	props: {
		form: {},
	},
	data() {
		return {}
	},
  mixins: [city, freeDelivery],
	computed: {
		items() {
			return this.checkBasketItemsAvailability(this.$store.state.session?.basket);
		},
		destination() {
			return destinations.find(el => el.id === this.form.destinationId);
		},
		deliveryType() {
			return deliveryTypes.find(el => el.id === this.form.deliveryTypeId);
		},
		deliveryZone() {
			return this.destination?.zones?.find(el => el.id === this.form.deliveryZoneId);
		},
    // "Открытка" - полная цена открытки, без вычета скидки по купону (скидка отдельно
    // учитывается в "Ваша скидка")
    postcardPrice() {
      return Number(this.form.POSTCARD_PRICE) || 0;
    },
    // скидка по купону/скидочной карте на открытку
    postcardCouponDiscount() {
      if (!this.form.couponDiscount) return 0;
      return this.postcardPrice / 100 * Number(this.form.couponDiscount);
    },
    // цена открытки за вычетом скидки по купону - используется только для расчёта ИТОГО
		postcardsCost() {
			return this.postcardPrice - this.postcardCouponDiscount;
		},
		itemsCost() {
      // "Ваш заказ" - сумма БЕЗ скидки по купону/скидочной карте (полная цена товаров).
      // Бесплатные позиции (скидка 100%, напр. конфета за опрос) не входят ни сюда, ни в "Ваша скидка"
      return itemsFullPrice(this.items);
		},
    // суммарная скидка по товарам корзины (напр. бесплатная конфета за прохождение опроса),
    // отдаётся бэкендом (поле DISCOUNT). Не отображается отдельно - см. комментарий к discount() ниже
    itemsDiscount() {
      return this.items.reduce((sum, item) => {
        if (!item.isProductAvailable) return sum;
        if (isFreeItem(item)) return sum;
        // DISCOUNT - за единицу товара, как и в снимке купона (см. itemsCouponDiscount)
        return sum + (Number(item.DISCOUNT) || 0) * Number(item.AMOUNT);
      }, 0);
    },
    // скидка по купону/скидочной карте на сами товары (без открытки)
    // discountBasket - это снимок цен на момент проверки купона (ключ - ID товара в корзине).
    // Считаем по актуальному составу корзины (this.items), а не по снимку,
    // чтобы удалённые/добавленные после проверки купона товары не ломали сумму.
    itemsCouponDiscount() {
      if (!this.form.discountBasket) return 0;

      let totalDiscount = 0;
      for (const item of this.items) {
        if (!item.isProductAvailable) continue;
        if (!(item.AMOUNT > 0)) continue;
        if (isFreeItem(item)) continue;

        const discountEntry = this.form.discountBasket[item.ID];
        if (!discountEntry) continue;
        totalDiscount += Number(discountEntry.DISCOUNT) * Number(item.AMOUNT);
      }

      return totalDiscount;
    },
    // Итоговая скидка на товары, которую нужно вычесть из itemsCost.
    // itemsDiscount (бэкенд, поле DISCOUNT) и itemsCouponDiscount (фронтенд, снимок CheckCoupon)
    // могут пересекаться: как только после проверки купона происходит любой другой запрос,
    // трогающий корзину (смена кол-ва, повторная загрузка страницы и т.п.), Bitrix пересчитывает
    // DISCOUNT заново, и оно начинает включать в себя купон тоже (см. SessionController::initBasket) -
    // простое вычитание обеих сумм задвоило бы скидку. Берём максимум: пока backend не пересчитал -
    // используем свежий фронтенд-снимок купона, как только пересчитал - его число (уже включает всё).
    productsDiscount() {
      return Math.max(this.itemsDiscount, this.itemsCouponDiscount);
    },
    // "Ваша скидка" - единая скидка по заказу: товары (по правилам корзины + купон вместе,
    // см. productsDiscount) и открытка. Раньше показывалась двумя отдельными строками
    // ("Ваша скидка" = itemsDiscount и "Ваша выгода" = скидка по купону), но после пересчёта
    // бэкендом корзины (см. productsDiscount) они обычно совпадают - оставили одну строку.
    discount() {
      return this.productsDiscount + this.postcardCouponDiscount;
    },
		total() {
			const deliveryCost = this.deliveryCost;
			if (deliveryCost === null) return null;

      // itemsCost - полная цена товаров, postcardsCost уже посчитан с учётом скидки купона на открытку
			const productsTotal = this.itemsCost - this.productsDiscount;
			const postcardCost = this.postcardsCost;

			return productsTotal + deliveryCost + postcardCost;
		},
    prepaymentNotificationText() {
      return this.$store.state.common?.texts?.PREPAYMENT_NOTIFICATION?.DETAIL_TEXT.trim();
    },
	}
}
</script>

<style lang="scss">

.basket-form-cost {
	// сообщение о бесплатной доставке - того же размера, что и строки "Ваш заказ", "Доставка"
	.free-delivery-message {
		margin: 4px 0 0;
		font-size: inherit;
		line-height: 20px;
	}

	&__note {
		border: 2px solid $golden;
		//background: $golden-light;
		padding: 20px;
	}

  &__prepayment-notification {
    letter-spacing: 0;
    width: 100%;
    max-width: 250px;
    margin: 0 auto;
  }
}
</style>